import React, { useState } from 'react';
import type { Dispatch } from 'redux';
import { useDispatch } from 'react-redux';
import { actions as locationTaskActions } from '@Actions/locationTaskActions';
import Notification from '@Common/Notification';
import RadioGroup from '@Common/RadioGroup/RadioGroup';
import Modal61 from '@Common/Modal/Modal61';
import { FEEDBACK_OPTIONS } from '@Constants/constants';
import { CREATE_FEEDBACK_SUCCESS, CREATE_FEEDBACK_FAILURE } from '@Constants/actionTypes';
import TextArea from '@Components/Common/TextArea';
import type { Modal61Ref } from '@Types/common';

interface FeedbackModalProps {
    selectedFeedbackOption: string;
    setSelectedFeedbackOption: (value: string) => void;
    feedbackText: string;
    setFeedbackText: (value: string) => void;
    handleCloseFeedbackModal: () => void;
    locationTaskId: number;
    feedbackModalRef: React.RefObject<Modal61Ref | null>;
    hasBaselineTaskLink?: boolean;
}

const FeedbackModal =
    ({ selectedFeedbackOption,
        setSelectedFeedbackOption,
        feedbackText,
        setFeedbackText,
        handleCloseFeedbackModal,
        locationTaskId,
        feedbackModalRef,
        hasBaselineTaskLink = false
    }: FeedbackModalProps) => {
        const dispatch: Dispatch = useDispatch();
        const [isSubmitting, setIsSubmitting] = useState(false);

        // Filter feedback options based on whether there's a baseline task link
        const filteredFeedbackOptions = FEEDBACK_OPTIONS.filter(option => {
            // Option "5" is "The link is not correct" - only show when there's a link
            if (option.id === '5') {
                return hasBaselineTaskLink;
            }
            return true;
        });

        const submitFeedback = async (request) => dispatch<any>(locationTaskActions.createFeedback(request));
        const handleSubmitFeedback = async () => {
            if (isSubmitting) return; // Prevent multiple submissions

            if (selectedFeedbackOption.trim() === '') {
                Notification('error', 'Error', 'Please select a valid feedback option.');
                return;
            }
            const selectedOption = filteredFeedbackOptions.find(
                option => String(option.id) === selectedFeedbackOption
            );
            if (!selectedOption) {
                Notification('error', 'Error', 'Please select a valid feedback option.');
                return;
            }
            const feedbackChoice = Number(selectedOption.id);

            setIsSubmitting(true);
            try {
                const feedbackRequest = {
                    locationTaskId,
                    feedbackChoice,
                    text: feedbackText,
                };
                const response = await submitFeedback(feedbackRequest);
                if (response.type === CREATE_FEEDBACK_SUCCESS) {
                    Notification('success', 'Success', 'Thank you for your feedback!');
                    handleCloseFeedbackModal();
                } else if (response.type === CREATE_FEEDBACK_FAILURE) {
                    const failureMessage = typeof response?.payload?.message === 'string' && response.payload.message.trim() !== ''
                        ? response.payload.message
                        : 'Failed to submit feedback. Please try again.';
                    Notification('error', 'Error', failureMessage);
                } else {
                    Notification('error', 'Error', 'Unexpected response received while submitting feedback.');
                }
            } catch (error) {
                Notification('error', 'Error', error);
            } finally {
                setIsSubmitting(false);
            }
        };


        return <Modal61
            ref={feedbackModalRef}
            title='Suggestions'
            isLoading={isSubmitting}
            body={(
                <div style={{ padding: '20px' }}>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '10px',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            textAlign: 'center'
                        }}>
                            Please select an option:
                        </label>
                        <RadioGroup
                            defaultValue=''
                            ariaLabel="Feedback options"
                            name="feedbackOptions"
                            options={filteredFeedbackOptions.map(option => ({
                                value: option.id,
                                label: option.label
                            }))}
                            value={selectedFeedbackOption}
                            onValueChange={setSelectedFeedbackOption}
                            centered={true}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '10px',
                            fontWeight: 'bold',
                            fontSize: '14px'
                        }}
                            htmlFor="feedbackText">
                            Additional comments (optional):
                        </label>
                        <TextArea
                            rows={4}
                            name="feedbackText"
                            label=""
                            value={feedbackText}
                            isRequired={false}
                            onChange={(e) => setFeedbackText(e.target.value)}
                        />
                    </div>
                </div>
            )}
            customFooter={(
                <div className='mx-auto'>
                    <button
                        type='button'
                        className='btn btn-primary me-3'
                        onClick={handleSubmitFeedback}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                    <button
                        type='button'
                        className='btn btn-light btn-outline-dark'
                        onClick={handleCloseFeedbackModal}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                </div>
            )}
            onCloseClick={handleCloseFeedbackModal}
        />
    };

export default FeedbackModal;